import { ScrollableNavigationBar } from "@/components/pure/navigation/dfp-scroll-navigation";
import { SubmissionDetail } from "@/components/pure/submission-detail/submissions-detail";
import { DB_ORG } from "@/constants/db";
import { CushmanDataUpdateTab } from "@/layouts/signed-in/views/Admin/CushmanDataUpdateTab/CushmanDataUpdateTab";
import { IntegrationsTab } from "@/layouts/signed-in/views/Admin/IntegrationsTab/IntegrationsTab";
import { MonthlyAuditTab } from "@/layouts/signed-in/views/Admin/MonthlyAuditTab/MonthlyAuditTab";
import { SettingsTab } from "@/layouts/signed-in/views/Admin/SettingsTab/SettingsTab";
import { SubscriptionTab } from "@/layouts/signed-in/views/Admin/SubscriptionTab/SubscriptionTab";
import { SyncSettingsTab } from "@/layouts/signed-in/views/Admin/SyncSettingsTab/SyncSettingsTab";
import { UsersTable } from "@/layouts/signed-in/views/Admin/UsersTable/UsersTable";
import { SubmissionsTab } from "@/layouts/signed-in/views/SingleLocation/SubmissionsTab/SubmissionsTab";
import { checkIntegrationEnabled } from "@/services/integration-service";
import { useAdminStore } from "@/stores/admin-store";
import { useUserStore } from "@/stores/user-store";
import { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./Admin.scss";

const Admin = () => {
	const { user } = useUserStore();
	const location = useLocation();
	const {
		isIntegrationEnabled,
		setIsIntegrationEnabled,
		integrationCheckedForUserId,
		setIntegrationCheckedForUserId,
		integrationCheckedForOrgId,
		setIntegrationCheckedForOrgId,
	} = useAdminStore();
	const isCheckingIntegration = useRef(false);
	const checkTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	const checkIntegration = async () => {
		// Clear any pending checks
		if (checkTimeoutRef.current) {
			clearTimeout(checkTimeoutRef.current);
		}

		// Prevent concurrent calls
		if (isCheckingIntegration.current) {
			return;
		}

		// Check if we've already checked for this user AND organization combination
		const currentUserId = user?.id;
		const currentOrgId = user?.organization?.id || user?.organization_id;

		if (
			integrationCheckedForUserId === currentUserId &&
			integrationCheckedForOrgId === currentOrgId &&
			isIntegrationEnabled !== null
		) {
			return;
		}

		if (!currentUserId || !currentOrgId) {
			return;
		}

		isCheckingIntegration.current = true;

		try {
			const isEnabled = await checkIntegrationEnabled("1");
			setIsIntegrationEnabled(isEnabled);
			setIntegrationCheckedForUserId(currentUserId);
			setIntegrationCheckedForOrgId(currentOrgId);
		} catch (error) {
			console.error("Error checking integration status:", error);
			setIsIntegrationEnabled(false);
		} finally {
			isCheckingIntegration.current = false;
		}
	};

	// Helper function to check if trial is expired
	const isTrialExpired = (): boolean => {
		if (!user?.is_trial && !user?.isTrial && !user?.isTrialAccount) {
			return false;
		}

		const trialExpirationDate =
			user?.trialExpirationDate || user?.trial_expiration_date;
		if (!trialExpirationDate) {
			// If no expiration date, check if account is disabled as fallback
			return user?.isAccountDisabled || false;
		}

		const expirationDate = new Date(trialExpirationDate);
		const now = new Date();
		return expirationDate <= now;
	};

	useEffect(() => {
		const currentOrgId = user?.organization?.id || user?.organization_id;

		// Only check if we haven't checked for this user+org combination yet
		if (
			user?.id &&
			currentOrgId &&
			(integrationCheckedForUserId !== user.id ||
				integrationCheckedForOrgId !== currentOrgId ||
				isIntegrationEnabled === null)
		) {
			checkTimeoutRef.current = setTimeout(() => {
				checkIntegration();
			}, 100); // Small delay to prevent rapid calls
		}

		return () => {
			if (checkTimeoutRef.current) {
				clearTimeout(checkTimeoutRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, user?.organization?.id, user?.organization_id]); // Check when user ID or org ID changes

	// Build tab names array based on organization and user role
	let tabNames: string[] = ["Users", "Submissions"];

	// Add organization-specific tabs
	if (user?.organization?.external_firebase_id === DB_ORG.CUSHMANWAKEFIELD) {
		tabNames.push("Data Update", "Monthly Audit");
	}

	// Add standard tabs for all users
	tabNames.push("Integrations", "Settings", "Subscription");

	// Allow all authenticated users to access admin panel
	if (!user?.role) {
		return <Navigate to="/plans" replace />;
	}

	// Redirect expired trial users to subscription tab
	if (isTrialExpired() && location.pathname !== "/admin/subscription") {
		return <Navigate to="/admin/subscription" replace />;
	}

	return (
		user?.organization && (
			<div className="admin-container px-2">
				<ScrollableNavigationBar basePath={"/admin"} tabNames={tabNames} />
				<div className="fullSize">
					<Routes>
					<Route
						path="/"
						element={
							<Navigate
								to={
									isTrialExpired()
										? "/admin/subscription"
										: "/admin/users"
								}
								replace
							/>
						}
					/>
						{!isTrialExpired() && (
							<>
								<Route path="users" element={<UsersTable />} />
								<Route
									path="submissions"
									element={<SubmissionsTab organization={user?.organization} />}
								/>
								<Route
									path="submissions/:submissionId"
									element={<SubmissionDetail />}
								/>
								<Route
									path="data-update"
									element={
										<CushmanDataUpdateTab organization={user?.organization} />
									}
								/>
								<Route
									path="monthly-audit"
									element={
										<MonthlyAuditTab organization={user?.organization} />
									}
								/>
								<Route
									path="integrations"
									element={<IntegrationsTab />}
								/>
								<Route
									path="integrations/:integrationId/sync"
									element={<SyncSettingsTab />}
								/>
								<Route
									path="settings"
									element={<SettingsTab />}
								/>
							</>
						)}
						<Route path="subscription" element={<SubscriptionTab />} />
					<Route
						path="*"
						element={
							<Navigate
								to={
									isTrialExpired()
										? "/admin/subscription"
										: "/admin/users"
								}
								replace
							/>
						}
					/>
					</Routes>
				</div>
			</div>
		)
	);
};

export { Admin };
